export type USdtOpts = {
  withTime?: boolean;
  withSeconds?: boolean;
  hour12?: boolean;
  timeZone?: string;
  timeZoneName?: "short" | "long";
  dateTimeSeparator?: string;
};

export function formatUSWithDash(iso: string | number | Date | null | undefined, opts: USdtOpts = {}): string {
  if (!iso) return "";
  let dateValue = iso;

  if (typeof iso === "string") {
    dateValue = normalizeIsoToUtc(iso);
  }

  const d = dateValue instanceof Date ? dateValue : new Date(dateValue);
  if (Number.isNaN(d.getTime())) return "";

  const {
    withTime = false,
    withSeconds = false,
    hour12 = true,
    timeZone = "UTC",
    timeZoneName,
    dateTimeSeparator = ", ",
  } = opts;

  const dateParts = new Intl.DateTimeFormat("en-US", {
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    timeZone,
  })
    .formatToParts(d)
    .reduce<Record<string, string>>((acc, p) => {
      if (p.type === "year" || p.type === "month" || p.type === "day") acc[p.type] = p.value;
      return acc;
    }, {});
  const dateStr = `${dateParts.month}-${dateParts.day}-${dateParts.year}`;

  if (!withTime) return dateStr;

  const timeOpts: Intl.DateTimeFormatOptions = {
    hour: "numeric",
    minute: "2-digit",
    ...(withSeconds ? { second: "2-digit" } : {}),
    hour12,
    timeZone,
    ...(timeZoneName ? { timeZoneName } : {}),
  };
  const timeStr = new Intl.DateTimeFormat("en-US", timeOpts).format(d).replace(/\u202F|\u00A0/g, " ");

  return `${dateStr}${dateTimeSeparator}${timeStr}`;
}

function normalizeIsoToUtc(iso: string): string {
  if (/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}/.test(iso) && !/[zZ]|[+-]\d{2}:?\d{2}$/.test(iso)) {
    return iso + "Z";
  }
  return iso;
}

export const formatUSDate = (iso: string | number | Date, timeZone = "UTC") =>
  formatUSWithDash(iso, { withTime: false, timeZone });

export const formatUSLocalDateTime = (iso: string | number | Date, withSeconds = false) =>
  formatUSWithDash(iso, {
    withTime: true,
    withSeconds,
    timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone,
  });
